import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { dashboardTokens } from '../../../Dashboard/Components/dashboardCardStyles';
import { formatCurrencyOrDash } from '../../components/leadsMeta';
import { PlatformMetrics } from '../../../../services/marketingLeads';

const GOOD = '#22c55e';
const BAD = '#ef4444';

export interface MetaKpiInput {
  totalLeads: number;
  qualified: number;
  contacted: number;
  costPerLead: number;
  amountSpent: number;
  changePct: PlatformMetrics['changePct'] | null;
  /** ISO 4217 of the connected ad account — the money tiles are in it. */
  currency?: string;
  /**
   * False = the insights sync stored no campaign-days for this range, so the
   * money tiles show "—". A synced zero and an unsynced range look identical
   * once both render as "£0", and only one of them is a fact.
   */
  hasSpendData?: boolean;
  /** Metrics we derive from lead records rather than the ad platform. */
  derivedNote?: string;
}

interface Tile {
  key: string;
  label: string;
  value: string;
  changePct: number | null;
  higherIsBetter: boolean;
  icon: React.ReactNode;
  iconColor: string;
  note?: string;
}

const Delta: React.FC<{ changePct: number | null; higherIsBetter: boolean }> = ({
  changePct,
  higherIsBetter,
}) => {
  if (changePct === null) {
    return (
      <Typography sx={{ fontSize: 12, color: dashboardTokens.textMuted }}>
        no comparison data
      </Typography>
    );
  }

  const isIncrease = changePct >= 0;
  const isGood = isIncrease === higherIsBetter;
  const color = changePct === 0 ? dashboardTokens.textMuted : isGood ? GOOD : BAD;
  const Arrow = isIncrease ? ArrowDropUpIcon : ArrowDropDownIcon;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 600, color }}>
      {changePct !== 0 && <Arrow sx={{ fontSize: 16 }} />}
      <span>{Math.abs(changePct)}%</span>
      <Box component="span" sx={{ color: dashboardTokens.textMuted, fontWeight: 500, ml: 0.5 }}>
        vs previous period
      </Box>
    </Box>
  );
};

const StatTile: React.FC<{ tile: Tile }> = ({ tile }) => (
  <Box
    sx={{
      flex: '1 1 190px',
      minWidth: 180,
      bgcolor: '#1c1c1c',
      border: `1px solid ${dashboardTokens.border}`,
      borderRadius: 3,
      p: 2,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Typography sx={{ fontSize: 13, color: dashboardTokens.textSecondary, fontWeight: 500 }}>
        {tile.label}
      </Typography>
      <Tooltip title={tile.note || ''} disableHoverListener={!tile.note}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${tile.iconColor}22`,
            color: tile.iconColor,
          }}
        >
          {tile.icon}
        </Box>
      </Tooltip>
    </Box>
    <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1.1, mb: 0.75 }}>
      {tile.value}
    </Typography>
    <Delta changePct={tile.changePct} higherIsBetter={tile.higherIsBetter} />
  </Box>
);

export const MetaKpiTiles: React.FC<{ input: MetaKpiInput }> = ({ input }) => {
  const currency = input.currency || 'GBP';
  const hasSpend = input.hasSpendData !== false;
  const spendNote = hasSpend
    ? undefined
    : 'No ad spend has been synced for this date range. Run "Import history" on the connection card, or check that the chosen ad account is the one running these campaigns.';

  const tiles: Tile[] = [
    {
      key: 'total',
      label: 'Total Leads',
      value: String(input.totalLeads),
      changePct: input.changePct?.leads ?? null,
      higherIsBetter: true,
      icon: <PeopleAltOutlinedIcon fontSize="small" />,
      iconColor: '#a78bfa',
    },
    {
      key: 'qualified',
      label: 'Qualified Leads',
      value: String(input.qualified),
      changePct: null,
      higherIsBetter: true,
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      iconColor: '#22c55e',
      note: 'Counted from lead status (Qualified or Converted) for leads received in this range.',
    },
    {
      key: 'contacted',
      label: 'Contacted',
      value: String(input.contacted),
      changePct: null,
      higherIsBetter: true,
      icon: <ForumOutlinedIcon fontSize="small" />,
      iconColor: '#f59e0b',
      note: 'Counted from lead status for leads received in this range.',
    },
    {
      key: 'cpl',
      label: 'Cost per Lead',
      value: formatCurrencyOrDash(input.costPerLead, currency, hasSpend),
      changePct: hasSpend ? input.changePct?.costPerLead ?? null : null,
      higherIsBetter: false,
      icon: <PaidOutlinedIcon fontSize="small" />,
      iconColor: '#60a5fa',
      note:
        spendNote ||
        'Derived from ad spend ÷ leads. Requires a connected ad account with spend.',
    },
    {
      key: 'spent',
      label: 'Amount Spent',
      value: formatCurrencyOrDash(input.amountSpent, currency, hasSpend),
      changePct: hasSpend ? input.changePct?.amountSpent ?? null : null,
      higherIsBetter: false,
      icon: <TrendingUpIcon fontSize="small" />,
      iconColor: '#ef4444',
      note: spendNote || 'Synced daily from Meta Ads insights.',
    },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {tiles.map((tile) => (
        <StatTile key={tile.key} tile={tile} />
      ))}
    </Box>
  );
};
